import {
  CATEGORIAS_CON_SEGUIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN,
  type EdicionDeTipoDeCumplimiento,
  type TipoDeDocumentoDeCumplimiento,
} from '@cnc/contracts';
import {useState} from 'react';
import {
  useAgregarTiposSugeridos,
  useReconocerArchivos,
  useCrearTipoDeCumplimiento,
  useEditarTipoDeCumplimiento,
  useTiposDeCumplimiento,
} from '../api/cumplimiento';
import {et, tr, type Texto} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_TIPOS as AT} from './ayuda-cumplimiento';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

const campo = 'rounded-md border border-linea bg-white px-2 py-1 text-sm';

// Cumplimiento → Tipos de documento: lo que se exige en cada categoria, cuanto
// dura desde la recepcion, cuando avisar, quien responde y las palabras que
// reconocen el tipo en el nombre del archivo. No se borran: se desactivan.
export function TiposDeCumplimiento() {
  const lista = useTiposDeCumplimiento();
  const sugeridos = useAgregarTiposSugeridos();
  const reconocer = useReconocerArchivos();
  const permisos = usePermisos();
  const [conDesactivados, setConDesactivados] = useState(false);
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);
  const tipos = lista.data?.tipos ?? [];
  return (
    <div className="space-y-5" data-prueba="cumplimiento-tipos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-3xl text-sm text-apagado">
          {tr(
            'Qué documentos se exigen en cada categoría. Vencimiento = fecha de recepción + vigencia (vacía = no vence: escribe la fecha en el documento). Los días de aviso deciden desde cuándo sale "por vencer". Las palabras clave reconocen el tipo en el nombre del archivo al cargar la carpeta. Un tipo inactivo deja de exigirse y conserva sus documentos.',
            'Which documents each category requires. Expiry = received date + validity (empty = never: write the date on the document). Warning days decide when it shows "expiring soon". Keywords recognize the type in the file name when loading the folder. An inactive type is no longer required and keeps its documents.',
          )}
        </p>
        {permisos.editar && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={sugeridos.isPending}
              onClick={() => sugeridos.mutate()}
              title={tr(
                'Agrega lo que falte de la lista sugerida y la actualiza; lo que alguien cambió a mano no se toca',
                'Adds what is missing from the suggested list and updates it; what someone changed by hand is kept',
              )}
              className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro disabled:opacity-50"
            >
              ✚ {tr('Agregar o actualizar la lista sugerida', 'Add or update the suggested list')}
            </button>
            <button
              type="button"
              disabled={reconocer.isPending}
              onClick={() => reconocer.mutate()}
              title={tr(
                'Vuelve a reconocer por el nombre los archivos de la carpeta sin tipo o con un tipo desactivado',
                'Recognizes again by name the folder files without a type or with a deactivated type',
              )}
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
            >
              ↻ {tr('Volver a reconocer archivos', 'Recognize files again')}
            </button>
          </div>
        )}
      </div>
      {sugeridos.isError && <p className="text-sm text-alerta">{sugeridos.error.message}</p>}
      {reconocer.data && (
        <p className="text-sm text-verde-oscuro">
          {tr(
            `Se revisaron ${numero(reconocer.data.revisados)} archivos y se reconoció el tipo de ${numero(reconocer.data.reconocidos)}.`,
            `${numero(reconocer.data.revisados)} files were checked and the type of ${numero(reconocer.data.reconocidos)} was recognized.`,
          )}
        </p>
      )}
      {tipos.some(t => !t.activo) && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={conDesactivados} onChange={e => setConDesactivados(e.target.checked)} />
          {tr('Mostrar también los tipos desactivados', 'Also show deactivated types')}
          <NotaDeAyuda texto={AT.conDesactivados} dentroDeEtiqueta />
        </label>
      )}
      {lista.isError && <p className="text-sm text-alerta">{lista.error.message}</p>}
      {CATEGORIAS_CON_SEGUIMIENTO.map(c => (
        <section key={c} className="overflow-x-auto rounded-lg border border-linea bg-white p-3">
          <h2 className="text-sm font-semibold tracking-wide text-apagado uppercase">{categorias[c]}</h2>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-apagado">
                <Encabezado titulo={tr('Documento', 'Document')} ayuda={AT.documento} />
                <Encabezado titulo={tr('Equivalente del exterior', 'Foreign equivalent')} ayuda={AT.equivalente} />
                <Encabezado titulo={tr('Obligatorio', 'Required')} ayuda={AT.obligatorio} />
                <Encabezado titulo={tr('Vigencia (meses)', 'Validity (months)')} ayuda={AT.vigencia} />
                <Encabezado titulo={tr('Avisar (días antes)', 'Warn (days before)')} ayuda={AT.aviso} />
                <Encabezado titulo={tr('Responsable', 'Responsible')} ayuda={AT.responsable} />
                <Encabezado titulo={tr('Palabras clave', 'Keywords')} ayuda={AT.palabras} />
                <Encabezado
                  titulo={tr('Archivos', 'Files')}
                  ayuda={AT.archivos}
                  clase="py-1 pr-2 text-right font-semibold"
                />
                <Encabezado titulo={tr('Activo', 'Active')} ayuda={AT.activo} clase="py-1 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {tipos
                .filter(t => t.categoria === c && (conDesactivados || t.activo))
                .map(t => (
                  <FilaDeTipo
                    key={t.id}
                    t={t}
                    responsables={lista.data?.responsables ?? []}
                    editable={permisos.editar}
                  />
                ))}
            </tbody>
          </table>
          {permisos.editar && <NuevoTipo categoria={c} />}
        </section>
      ))}
    </div>
  );
}

function Encabezado({
  titulo,
  ayuda,
  clase = 'py-1 pr-2 font-semibold',
}: {
  titulo: string;
  ayuda: Texto;
  clase?: string;
}) {
  return (
    <th className={clase}>
      {titulo}
      <NotaDeAyuda titulo={titulo} texto={ayuda} />
    </th>
  );
}

function FilaDeTipo({
  t,
  responsables,
  editable,
}: {
  t: TipoDeDocumentoDeCumplimiento;
  responsables: Array<{id: number; nombre: string}>;
  editable: boolean;
}) {
  const editar = useEditarTipoDeCumplimiento();
  const guardar = (edicion: Omit<EdicionDeTipoDeCumplimiento, 'version'>) =>
    editar.mutate({id: t.id, edicion: {version: t.version, ...edicion}});
  const entero = (valor: string) => (valor.trim() ? Math.max(1, Math.round(Number(valor))) : null);
  return (
    <tr className={`border-t border-linea/70 align-top ${t.activo ? '' : 'opacity-60'}`}>
      <td className="py-1.5 pr-2">
        <div className="font-medium">{t.nombre}</div>
        {t.descripcion && <div className="text-xs text-apagado">{t.descripcion}</div>}
        {editar.isError && <div className="text-xs text-alerta">{editar.error.message}</div>}
      </td>
      <td className="py-1.5 pr-2">
        <input
          aria-label={tr(`${t.nombre}: equivalente del exterior`, `${t.nombre}: foreign equivalent`)}
          defaultValue={t.nombreInternacional ?? ''}
          key={`i${t.version}`}
          disabled={!editable}
          placeholder={tr('El mismo', 'The same')}
          onBlur={e => {
            const valor = e.target.value.trim() || null;
            if (valor !== t.nombreInternacional) guardar({nombreInternacional: valor});
          }}
          className={`${campo} w-48 text-xs`}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          type="checkbox"
          aria-label={tr(`${t.nombre}: obligatorio`, `${t.nombre}: required`)}
          checked={t.obligatorio}
          disabled={!editable || editar.isPending}
          onChange={e => guardar({obligatorio: e.target.checked})}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          type="number"
          min={1}
          aria-label={tr(`${t.nombre}: vigencia en meses`, `${t.nombre}: validity in months`)}
          defaultValue={t.vigenciaMeses ?? ''}
          key={`v${t.version}`}
          disabled={!editable}
          placeholder="—"
          onBlur={e => {
            const valor = entero(e.target.value);
            if (valor !== t.vigenciaMeses) guardar({vigenciaMeses: valor});
          }}
          className={`${campo} w-20`}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          type="number"
          min={1}
          aria-label={tr(`${t.nombre}: días de aviso`, `${t.nombre}: warning days`)}
          defaultValue={t.diasDeAviso}
          key={`d${t.version}`}
          disabled={!editable}
          onBlur={e => {
            const valor = entero(e.target.value);
            if (valor && valor !== t.diasDeAviso) guardar({diasDeAviso: valor});
          }}
          className={`${campo} w-20`}
        />
      </td>
      <td className="py-1.5 pr-2">
        <select
          aria-label={tr(`${t.nombre}: responsable`, `${t.nombre}: responsible`)}
          value={t.responsableId ?? ''}
          disabled={!editable || editar.isPending}
          onChange={e => guardar({responsableId: e.target.value ? Number(e.target.value) : null})}
          className={campo}
        >
          <option value="">{tr('Sin responsable', 'No one')}</option>
          {responsables.map(r => (
            <option key={r.id} value={r.id}>
              {r.nombre}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1.5 pr-2">
        <input
          aria-label={tr(`${t.nombre}: palabras clave`, `${t.nombre}: keywords`)}
          defaultValue={t.palabrasClave.join(', ')}
          key={`p${t.version}`}
          disabled={!editable}
          title={tr('Separadas por comas', 'Comma separated')}
          onBlur={e => {
            const palabras = e.target.value
              .split(',')
              .map(p => p.trim())
              .filter(p => p.length >= 2);
            if (palabras.join(',') !== t.palabrasClave.join(',')) guardar({palabrasClave: palabras});
          }}
          className={`${campo} w-64 text-xs`}
        />
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums">{numero(t.documentos)}</td>
      <td className="py-1.5">
        <input
          type="checkbox"
          aria-label={tr(`${t.nombre}: activo`, `${t.nombre}: active`)}
          checked={t.activo}
          disabled={!editable || editar.isPending}
          onChange={e => guardar({activo: e.target.checked})}
        />
      </td>
    </tr>
  );
}

function NuevoTipo({categoria}: {categoria: (typeof CATEGORIAS_CON_SEGUIMIENTO)[number]}) {
  const crear = useCrearTipoDeCumplimiento();
  const [nombre, setNombre] = useState('');
  return (
    <form
      className="mt-2 flex flex-wrap items-center gap-2"
      onSubmit={e => {
        e.preventDefault();
        crear.mutate(
          {nombre, categoria, obligatorio: true, vigenciaMeses: 12, diasDeAviso: 60, palabrasClave: []},
          {onSuccess: () => setNombre('')},
        );
      }}
    >
      <input
        value={nombre}
        onChange={e => setNombre(e.target.value)}
        placeholder={tr('Nombre del documento nuevo', 'New document name')}
        aria-label={tr('Nombre del documento nuevo', 'New document name')}
        className={`${campo} w-72`}
      />
      <NotaDeAyuda titulo={tr('Nombre del documento nuevo', 'New document name')} texto={AT.nuevo} />
      <button
        type="submit"
        disabled={!nombre.trim() || crear.isPending}
        className="rounded-md bg-verde px-3 py-1 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
      >
        {tr('Agregar', 'Add')}
      </button>
      {crear.isError && <span className="text-xs text-alerta">{crear.error.message}</span>}
    </form>
  );
}
